import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { getOpenStatus, dayHasHours, type OperatingHours } from './openStatus'

export type ScheduleSlot = {
  /** Instante del pickup en UTC (ISO). Es lo que se guarda en orders.scheduled_for. */
  iso: string
  /** Etiqueta "HH:mm" (24h) en la zona del local. La UI la formatea a 12h. */
  label: string
}

export type ScheduleDay = {
  /** Fecha "yyyy-MM-dd" en la zona del local. */
  dateKey: string
  /** Día de la semana en inglés (monday..sunday), igual que operating_hours. */
  dayKey: string
  slots: ScheduleSlot[]
}

export type ScheduleOptions = {
  /** Minutos mínimos desde ahora hasta el primer slot disponible (tiempo de preparación). */
  leadMinutes: number
  /** Hasta cuántos días en el futuro se puede agendar (0 = solo hoy). */
  maxDaysAhead: number
  /** Separación entre slots, en minutos. Default 15. */
  intervalMinutes?: number
}

const DAY_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

function parseHM(value: string): number {
  const [h, m] = value.split(':').map((n) => parseInt(n, 10))
  return (h || 0) * 60 + (m || 0)
}

function toHM(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Fecha calendario en la zona del local desplazada `offset` días. */
function dateKeyForOffset(now: Date, timezone: string, offset: number): string {
  const today = formatInTimeZone(now, timezone, 'yyyy-MM-dd')
  const [y, m, d] = today.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + offset)
  return dt.toISOString().slice(0, 10)
}

/** Día de la semana (monday..sunday) de una fecha "yyyy-MM-dd". */
function dayKeyForDate(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number)
  return DAY_BY_INDEX[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
}

/**
 * Genera los slots de pickup disponibles para agendar un pedido, derivados del
 * horario de operación del local.
 *
 * Sin horario configurado devuelve [] (no se puede acotar el rango => no se
 * ofrece agendar). Solo contempla horarios dentro del mismo día calendario:
 * para un día que cruza medianoche (close < open) genera hasta medianoche, sin
 * extender al día siguiente (suficiente para pickup de restaurante; los locales
 * nightlife no usan este flujo).
 */
export function getScheduleSlots(
  operatingHours: OperatingHours | null,
  timezone: string,
  now: Date,
  opts: ScheduleOptions,
): ScheduleDay[] {
  if (!operatingHours || Object.keys(operatingHours).length === 0) return []

  const tz = timezone || 'America/Chicago'
  const interval = opts.intervalMinutes ?? 15
  // Piso ABSOLUTO. El lead time puede superar las 24h (catering pide 48-72h), así
  // que no basta con recortar el día 0: hay que comparar instante contra instante
  // en CADA día. Antes esto era un minuto-del-día aplicado solo a `offset === 0`,
  // y con lead de 48h los días 1 y 2 ofrecían slots que el servidor rechazaba —
  // `isValidScheduledFor` sí compara contra un instante absoluto, así que el
  // síntoma era confuso y no ruidoso: la UI ofrecía horas inválidas.
  // Con lead < 1 día el resultado es idéntico al anterior.
  const earliestMs = now.getTime() + opts.leadMinutes * 60_000

  const result: ScheduleDay[] = []

  for (let offset = 0; offset <= opts.maxDaysAhead; offset++) {
    const dateKey = dateKeyForOffset(now, tz, offset)
    const dayKey = dayKeyForDate(dateKey)
    const day = operatingHours[dayKey]
    // Salta días cerrados o con horario incompleto (`{closed:false}` sin horas).
    if (!dayHasHours(day)) continue

    const openMin = parseHM(day.open)
    const closeRaw = parseHM(day.close)
    // Cruce de medianoche: acotar al final del día (no extender al siguiente).
    const closeMin = closeRaw > openMin ? closeRaw : 24 * 60

    const slots: ScheduleSlot[] = []
    for (let m = openMin; m < closeMin; m += interval) {
      const label = toHM(m)
      const iso = fromZonedTime(`${dateKey}T${label}:00`, tz).toISOString()
      if (new Date(iso).getTime() < earliestMs) continue
      slots.push({ iso, label })
    }

    if (slots.length > 0) result.push({ dateKey, dayKey, slots })
  }

  return result
}

/**
 * Valida server-side un `scheduled_for` recibido del cliente: debe ser un
 * instante futuro (respetando el lead time), dentro del horizonte de días, y
 * caer dentro del horario de apertura del local. Defensa contra requests que
 * salten el selector de la UI.
 */
export function isValidScheduledFor(
  iso: string,
  operatingHours: OperatingHours | null,
  timezone: string,
  now: Date,
  opts: { leadMinutes: number; maxDaysAhead: number },
): boolean {
  const when = new Date(iso)
  const t = when.getTime()
  if (!Number.isFinite(t)) return false

  // Tolerancia de 60s para drift de reloj cliente/servidor.
  const minTime = now.getTime() + opts.leadMinutes * 60_000 - 60_000
  // Cota superior holgada: hasta el final del día maxDaysAhead.
  const maxTime = now.getTime() + (opts.maxDaysAhead + 1) * 86_400_000
  if (t < minTime || t > maxTime) return false

  // La hora de pickup debe estar dentro del horario de apertura.
  return getOpenStatus(operatingHours, timezone || 'America/Chicago', 0, when).isOpen
}
